<template>
  <div>
  <v-dialog width="720" v-model="showDialog">
    <template v-slot:activator="{ on }">
      <v-btn
        fab
        small
        :outlined="scheduled"
        :color="color"
        :style="btnStyle"
        v-on="on"
        :class="buttonClasses"
        class="patient-overview-timeline__button"
        :disabled="!isValid"
      >
        {{ version }}
      </v-btn>
    </template>

    <v-card>
      <div class="clearfix">
        <v-btn
          class="pull-right btn-transparent"
          icon
          plain
          @click="showDialog = false"
        >
          <v-icon> mdi-close </v-icon>
        </v-btn>
      </div>
      <div class="pa-5">
        <p class="d-flex justify-space-around pb-5">
          {{ message }}
        </p>
        <div class="d-flex justify-space-around pb-5 flex-wrap">
          <v-btn
            color="primary"
            outlined
            :x-large="$vuetify.breakpoint.mdAndUp"
            @click="action"
            :disabled="actionDisabled"
            class="mt-2"
          >
            {{ confirmLabel }}
          </v-btn>

          <v-btn
            v-if="!scheduled"
            color="primary"
            outlined
            :x-large="$vuetify.breakpoint.mdAndUp"
            @click="reopenEncounter"
            :disabled="!reopenEnabled || hasReadOnlyAccess"
            class="mt-2"
          >
            {{ $capitalize($t("encounter.dialog.reopenVisit")) }}
          </v-btn>

          <v-btn
              v-if="showUndoSkipVisit"
              color="primary"
              outlined
              :disabled="!canUndoSkipVisit"
              :x-large="$vuetify.breakpoint.mdAndUp"
              @click="undoSkipVisit"
              class="mt-2"
          >
            {{ $capitalize($t("encounter.dialog.undoSkipVisit")) }}
          </v-btn>

          <div v-if="scheduled">
            <open-patient-record-button
              :askConfirmation="false"
              :label="
                $ucfirst(
                  $t(`patient.startvisit`, {
                    params: { visitNumber: currentVersion },
                  })
                )
              "
              :extraProps="{
                color: 'primary',
                outlined: true,
                text: false,
                class: 'mt-2',
                xLarge: $vuetify.breakpoint.mdAndUp,
              }"
            />
          </div>
          <v-btn
            v-if="scheduled"
            color="primary"
            outlined
            :x-large="$vuetify.breakpoint.mdAndUp"
            class="mt-2"
            @click="showDialog = false"
          >
            {{ $ucfirst($t("buttons.cancel", { catalog: "app_mobile" })) }}
          </v-btn>
        </div>
        <div v-if="!scheduled" class="my-12">
          <p class="d-flex justify-space-around pb-5">
            {{ messageEditQuestionnaires }}
          </p>

          <p class="d-flex justify-space-around font-weight-bold">
            {{ $capitalize($t("encounter.dialog.questionnairesBefore")) }}
          </p>
          <div class="d-flex justify-center pb-8 flex-wrap">
            <v-btn
              color="primary"
              outlined
              :x-large="$vuetify.breakpoint.mdAndUp"
              :disabled="!visitHasBeforeQuestionnaires || hasReadOnlyAccess"
              @click="() => sendReminderToPatient(false)"
              class="mr-5 mt-2"
            >
              {{ $capitalize($t("encounter.dialog.sendReminderPatient")) }}
            </v-btn>
            <v-btn
              color="primary"
              outlined
              :x-large="$vuetify.breakpoint.mdAndUp"
              @click="() => openQuestionnaireUrl(false)"
              :disabled="!visitHasBeforeQuestionnaires"
              class="mt-2"
            >
              {{ $capitalize($t("encounter.dialog.consultMyself")) }}
            </v-btn>
            <v-btn
                color="primary"
                outlined
                :x-large="$vuetify.breakpoint.mdAndUp"
                @click="() => loadEproStatus(false)"
                :disabled="!visitHasBeforeQuestionnaires"
                class="mt-2"
            >
              {{ $t("encounter.dialog.checkCompleteness") }}
            </v-btn>
          </div>
          <p class="d-flex justify-space-around font-weight-bold">
            {{ $capitalize($t("encounter.dialog.questionnairesAfter")) }}
          </p>
          <div class="d-flex justify-center flex-wrap">
            <v-btn
              color="primary"
              outlined
              :x-large="$vuetify.breakpoint.mdAndUp"
              :disabled="!visitHasAfterQuestionnaires || hasReadOnlyAccess"
              @click="() => sendReminderToPatient(true)"
              class="mr-5 mt-2"
            >
              {{ $capitalize($t("encounter.dialog.sendReminderPatient")) }}
            </v-btn>
            <v-btn
              color="primary"
              outlined
              :x-large="$vuetify.breakpoint.mdAndUp"
              @click="() => openQuestionnaireUrl(true)"
              :disabled="!visitHasAfterQuestionnaires"
              class="mt-2"
            >
              {{ $capitalize($t("encounter.dialog.consultMyself")) }}
            </v-btn>
            <v-btn
                color="primary"
                outlined
                :x-large="$vuetify.breakpoint.mdAndUp"
                @click="() => loadEproStatus(true)"
                :disabled="!visitHasAfterQuestionnaires"
                class="mt-2"
            >
              {{ $t("encounter.dialog.checkCompleteness") }}
            </v-btn>
          </div>
        </div>
        <div class="d-flex justify-space-around pb-5">
          <v-btn
            v-if="!scheduled"
            color="primary"
            outlined
            :x-large="$vuetify.breakpoint.mdAndUp"
            @click="showDialog = false"
          >
            {{ $ucfirst($t("buttons.cancel", { catalog: "app_mobile" })) }}
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-dialog>

  <v-dialog width="1000" v-model="showEproStatus">
    <v-card>
      <div class="d-flex justify-content-start pt-5 pb-2">
        <h2 class="ml-10">
          {{ $t('encounter.dialog.visit', { params: { visitNumber: version } }) }}
        </h2>
      </div>
      <div class="d-flex justify-space-around pb-5">
        <v-data-table
            v-if="eproStatusLines.length"
            v-model="eproStatusLines"
            :headers="[
                {
                  value: 'name',
                  text: this.$t(this.eproStatusLinesAfter ? 'encounter.dialog.questionnairesAfter' : 'encounter.dialog.questionnairesBefore'),
                }]"
            :items="eproStatusLines"
            :items-per-page="-1"
            item-key="key"
            disable-sort
            disable-filtering
            disable-pagination
            hide-default-footer
            class="show-epro-status-table"
        >
          <template v-slot:item.name="{ value, item }">
            <span :class="{'font-weight-bold': item.required}">
              <v-icon v-if="item.completed" class="mr-2" :color="$color.coro['status-green']">mdi-check-circle</v-icon>
              <v-icon v-else class="mr-2" :color="$color.coro['status-red']">mdi-close-circle</v-icon>
              {{ value }}{{ item.required ? ' (*)' : '' }}
            </span>
          </template>
        </v-data-table>

      </div>

      <div class="d-flex justify-space-around pb-5">
        <v-btn
            v-if="!scheduled"
            color="primary"
            outlined
            :x-large="$vuetify.breakpoint.mdAndUp"
            @click="showEproStatus = false"
        >
          {{ $ucfirst($t("general.close", { catalog: "app_mobile" })) }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { ROLE_ADMIN } from "../../../store/user/role-tree";
import OpenPatientRecordButton from "../Patient/OpenPatientRecordButton.vue";
import {updateSDM} from "../../../store/followup-session/actions";
import gql from "graphql-tag";

/**
 * Component TimelineButton.vue
 *
 * @module TimelineButton
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.4] app/src/APP_DASHBOARD/templates/components/Parts/TimelineButton.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  components: { OpenPatientRecordButton },
  name: "TimelineButton",
  props: {
    version: {
      type: Number,
      required: true,
    },
    status: {
      type: String,
      required: true,
    },
    color: {
      type: String,
      required: true,
    },
    scheduled: {
      type: Boolean,
      default: () => false,
    },
    btnStyle: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showDialog: false,
      showEproStatus : false,
      eproStatusLines : [],
      eproStatusLinesAfter : false,
    };
  },
  computed: {
    ...mapGetters({
      currentVersion: "patientSummary/currentVersion",
      patientId: "patientSummary/id",
      subjectId: "patientSummary/studyId",
      hasReadOnlyAccess: "user/hasReadOnlyAccess",
      SDMEncounters: 'patientSummary/SDMEncounters',
      getEncounter: "patientSummary/getEncounter",
    }),
    buttonClasses() {
      return {
        "patient-overview-timeline__visit": true,
        "patient-overview-timeline__visit--white-text": !this.scheduled,
      };
    },
    showUndoSkipVisit() {
      if (!this.SDMEncounters?.length || !this.version) {
        return false;
      }

      const status = this.getEncounter(this.version)?.status;

      return status === 'skipped' || status === 'done';
    },
    canUndoSkipVisit() {
      if (!this.SDMEncounters?.length) {
        return false;
      }

      const lastListedEncounter = this.SDMEncounters[this.SDMEncounters.length - 1]

      if (!(lastListedEncounter && lastListedEncounter.status === 'skipped')) {
        return false;
      }

      return lastListedEncounter.version === this.version
    },
    message() {
      if (this.scheduled) {
        return this.$ucfirst(this.$t("encounter.dialog.whatDoYouWantToDo"));
      }

      return this.$ucfirst(
        this.$t("encounter.dialog.edit", {
          params: { visitNumber: this.version },
        })
      );
    },
    messageEditQuestionnaires() {
      return this.$ucfirst(
        this.$t("encounter.dialog.editQuestionnaires", {
          params: { visitNumber: this.version },
        })
      );
    },
    confirmLabel() {
      if (this.scheduled) {
        return this.$ucfirst(this.$t("skip"));
      }

      return this.$ucfirst(this.$t("edit"));
    },
    visitHasBeforeQuestionnaires() {
      return this.$store.getters["questionnaires/visitHasBeforeQuestionnaires"](
        this.version
      );
    },
    visitHasAfterQuestionnaires() {
      return this.$store.getters["questionnaires/visitHasAfterQuestionnaires"](
        this.version
      );
    },
    reopenEnabled() {
      const currentVersion =
        this.$store.getters["patientSummary/currentVersion"];
      const isAdmin = this.$store.getters["user/isGranted"](ROLE_ADMIN);

      const lastVisit = this.getEncounter(7);

      //  The last closed version has been completed
      return (
        this.status === "done" &&
        // Special case: If current version is 7, only allow reopening 7 (not 6)
        ((this.version === 7 && currentVersion >= 7 && lastVisit) ||
          // Otherwise, allow reopening the previous version
          (this.version === currentVersion - 1 && !lastVisit)) &&
        // Only admin can reopen visit 1 (for debugging purposes)
        (this.version !== 1 || isAdmin) &&
        // If another encounter is active, then cannot re-open old one
        !this.$store.getters["patientSummary/getCurrentInProgressSDMEncounter"]
      );
    },
    actionDisabled() {
      if (this.scheduled) {
        return this.hasReadOnlyAccess;
      }

      return !this.$store.getters["user/canEditVisitData"](this.version);
    },
    isValid() {
      return !!(
        this.version <= this.$store.getters["patientSummary/currentVersion"]
      );
    },
  },
  methods: {
    async loadEproStatus(after = false) {
      this.eproStatusLinesAfter = after

      if (!this.version) {
        this.eproStatusLines = [];
        this.showEproStatus = false;
        return;
      }

      const collectionKey = after ? 'AFTER_' + this.version : 'BEFORE_' + this.version;

      const collection = await this.$app.questionnaireManager.getEProConfigurationCollection(
          this.subjectId,
          collectionKey,
          true
      );

      let eproStatusLines = [];

      if (collection?.questionnaires) {
        eproStatusLines = collection.questionnaires.map((questionnaire) => {
          const result = {
            key : questionnaire.key,
            name : questionnaire.internalName ? questionnaire.internalName :questionnaire.name,
            completed : false,
            required : collection.requiredQuestionnaireKeys.includes(questionnaire.key),
          }

          if (collection.forms) {
            for (let i=0; i<collection.forms.length; i++) {
              const form = collection.forms[i];

              if (form.questionnaire.key === questionnaire.key) {
                result.completed = form.allRequiredQuestionsCompleted
                break;
              }
            }
          }

          return result
        })
      }

      if (this.version > 1 && !after) {
          eproStatusLines.push({
            key : this.$t('medication.medicationPrescription'),
            name : 'Medication prescription',
            completed : await this.hasMedicationSchedules(),
            required : true,
          })
      }

      this.eproStatusLines = eproStatusLines;
      this.showEproStatus = true;
    },
    async openQuestionnaireUrl(after = false) {
      this.showDialog = false;

      const questionnaireUrl = await this.$store.dispatch(
        "questionnaires/loadQuestionnaireQRCodeToken",
        {
          chosenSubjectId: this.subjectId,
          chosenVersionNumber: this.version,
          chosenLocale: null,
          chosenUsername: this.subjectId,
          chosenBeforeAfter: after ? "after" : "before",
          initializer: "CASENURSE",
        }
      );

      window.open(questionnaireUrl);
    },

    async sendReminderToPatient(after = false) {
      this.showDialog = false;

      await this.$store.dispatch("alerts/sendReminderToPatient", {
        patientId: parseInt(this.patientId),
        reminderType: "FILL_IN_QUESTIONNAIRE",
        force: true,
        forceNotify: true,
        parameters: [
          {
            key: "version",
            value: this.version ? this.version.toString() : null,
          },
          { key: "timing", value: after ? "AFTER" : "BEFORE" },
        ],
      });
    },

    async hasMedicationSchedules() {
      if (!this.patientId) {
        return false;
      }

      const data = await this.$app.apolloMobileClient
          .query({
            query: gql`
            query medicationSchedules($patientId: String!) {
              myMedicationSchedules(
                functions: {function: patientId, parameters: [$patientId]},
                conditions : {field : deleted, value: "0"}
                limit: 1
              ) {
                id
              }
            }
          `,
            variables: {
              patientId: this.patientId.toString()
            },
          })

       return data?.data?.myMedicationSchedules?.length > 0;
    },

    editEncounter() {
      this.$router.push({
        name: "PatientEncounterVitalSigns",
        params: {
          version: this.version,
        },
      });
    },
    async reopenEncounter() {
      await this.$store.dispatch("followupSession/startSDM", {
        version: this.version,
      });
      this.$router.push({ name: "PatientRecordProgressStatusJourney" });
    },
    async undoSkipVisit() {
      await this.updateEncounterStatus('created');

      this.$toasted.show(
          this.$ucfirst(this.$t("successMessages.markedAsNotSkipped")),
          {
            type: "success",
            icon: "check",
            position: "bottom-center",
          }
      );
    },
    async updateEncounterStatus(status = 'skipped') {
      this.showDialog = false;

      const success = await this.$store.dispatch("followupSession/updateSDM", {
        patientId: this.patientId,
        version: this.version,
        status : status
      });

      if (success && status === 'skipped') {
          this.$toasted.show(
              this.$ucfirst(this.$t("successMessages.encounterMarkedAsSkipped")),
              {
                type: "success",
                icon: "check",
                position: "bottom-center",
              }
          );
      }
    },
    action() {
      if (this.scheduled) {
        this.updateEncounterStatus();
      } else {
        this.editEncounter();
      }
    },
  },
};
</script>

<style lang="scss">
.patient-overview-timeline__visit {
  background: #fff;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  border: 1px solid #ccc;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
  position: absolute;
  top: -12.5px;
  box-shadow: none;
  font-size: 16px !important;
  font-weight: 600 !important;
  box-shadow: none !important;
}

.patient-overview-timeline__visit--white-text {
  color: #fff !important;
}

.patient-overview-timeline__button {
  &.theme--light.v-btn.v-btn--disabled {
    color: rgba(0, 0, 0, 0.87) !important;
  }
}

.show-epro-status-table {
  th {
    font-size: 1.2em !important;
    color: #000 !important;
  }
  tr {
    background: none !important;
  }
}
</style>
